/**
 * A one-line status strip above the app when the subscription needs attention:
 * a trial counting down, a payment that failed and is inside its grace window,
 * or a plan set to end.
 *
 * Silent when the subscription is simply active — an athlete who is paid up
 * should never see billing chrome while training.
 */
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useSubscription } from "@/lib/useSubscription";

type Tone = "info" | "warn";

export function SubscriptionBanner() {
  const { t } = useTranslation();
  const { trialing, trialDaysLeft, inGrace, graceDaysLeft, endingSoon, subscription } =
    useSubscription();

  let message: string | null = null;
  let tone: Tone = "info";
  let ctaKey = "billing.banner.manage";

  if (inGrace) {
    message = t("billing.banner.past_due", { count: graceDaysLeft ?? 0 });
    tone = "warn";
    ctaKey = "billing.banner.update_payment";
  } else if (trialing && trialDaysLeft !== null) {
    // Below three days the trial is the athlete's problem today, not later.
    tone = trialDaysLeft <= 3 ? "warn" : "info";
    message =
      trialDaysLeft === 0
        ? t("billing.banner.trial_last_day")
        : t("billing.banner.trial_days", { count: trialDaysLeft });
  } else if (endingSoon && subscription?.current_period_end) {
    message = t("billing.banner.ending", {
      count: daysLeftOrZero(subscription.current_period_end),
    });
    tone = "warn";
    ctaKey = "billing.banner.resume";
  }

  if (!message) return null;

  return (
    <div
      role="status"
      className={[
        "flex flex-wrap items-center justify-center gap-x-3 gap-y-1 px-4 py-2 text-[13px]",
        tone === "warn"
          ? "bg-coral/10 text-coral"
          : "bg-amber/10 text-amber-dark",
      ].join(" ")}
    >
      <span>{message}</span>
      <Link to="/settings" className="font-medium underline underline-offset-2">
        {t(ctaKey)}
      </Link>
    </div>
  );
}

function daysLeftOrZero(iso: string): number {
  const d = Math.ceil((new Date(iso).getTime() - Date.now()) / 86_400_000);
  return Number.isNaN(d) ? 0 : Math.max(0, d);
}
